import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import useIsDarkTheme from "../../../core/hooks/useIsDarkTheme";
import acerteiLogoDark from "../../assets/logo/acertei-logo-dark.jpg";
import acerteiLogoLight from "../../assets/logo/acertei-logo-light.jpg";
import matteDark from "../../assets/logo/acertei-logo-dark.matte.png";
import matteLight from "../../assets/logo/acertei-logo-light.matte.png";
import "./Wordmark.css";

// Baked-in plate colors matching each JPG's own flat background (neither
// export has transparency) — literal hex tied to the asset's own baked
// background, not a design token; replaceable in one place if a transparent
// asset ever ships.
const DARK_PLATE = "#071E30";
const LIGHT_PLATE = "#FFFFFF";

export default function Wordmark({
  width,
  className,
  variant,
  to,
  label,
}: {
  width: number;
  className?: string;
  /** Pins the asset/plate to one variant instead of following the theme —
   * for a surface that doesn't itself follow the theme, e.g. the Sidebar's
   * always-navy rail (design.md, "the wordmark variant follows its surface,
   * not the theme"). Omit to keep following `useIsDarkTheme()`, as the
   * Footer and App Bar do — their surfaces do follow the theme. */
  variant?: "dark" | "light";
  /**
   * Destino do clique. Com `to`, o logotipo vira um link de verdade — com
   * foco por teclado, menu de contexto e "abrir em nova aba" — e ganha o
   * movimento de hover descrito em Wordmark.css. Sem `to`, é só a imagem.
   *
   * É um `Link`, e não um `button` com `navigate`, porque isto é navegação:
   * as duas superfícies que já eram clicáveis usavam `<div onClick>`, que não
   * recebe foco, não tem papel e não abre em nova aba.
   */
  to?: string;
  /** Texto acessível do link. Padrão: só "Acertei", sem o destino. */
  label?: string;
}) {
  const isDarkTheme = useIsDarkTheme();
  const isDark = variant ? variant === "dark" : isDarkTheme;
  const height = Math.round((width * 3) / 8);

  const src = isDark ? acerteiLogoDark : acerteiLogoLight;
  const plate = isDark ? DARK_PLATE : LIGHT_PLATE;
  const matte = isDark ? matteDark : matteLight;

  const imagem = (
    <img
      className="acertei-wordmark__img"
      src={src}
      alt={to ? "" : "Acertei"}
      width={width}
      height={height}
      style={{ width, height, display: "block" }}
      // A placa é desenhada no wrapper; arrastar a imagem para fora dela
      // mostraria o JPG com o fundo chapado solto, sem o contexto.
      draggable={false}
    />
  );

  const estilo = { width, height, backgroundColor: plate };

  if (!to) {
    return (
      <div
        className={cx("acertei-wordmark", className)}
        style={estilo}
        data-variant={isDark ? "dark" : "light"}
      >
        {imagem}
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={cx("acertei-wordmark", className)}
      style={
        {
          ...estilo,
          // A varredura do hover é recortada pela forma das letras, e a máscara
          // que faz esse recorte é a do asset que está na tela. Quem escolhe o
          // asset é este componente, então é aqui que a máscara entra — o CSS
          // só a consome. Ver o bloco da varredura em Wordmark.css.
          "--acertei-wordmark-matte": `url(${matte})`,
        } as CSSProperties
      }
      // O `alt` da imagem fica vazio de propósito: com ele preenchido o leitor
      // de tela anunciaria "Acertei" duas vezes dentro do mesmo link.
      aria-label={label ?? "Acertei"}
      // Qual dos dois assets está na tela. O webpack embute as duas máscaras
      // como `data:` (ambas abaixo do limite de inline), então o recorte em uso
      // não dá para identificar pela URL — e é justamente o pareamento
      // asset ↔ máscara que precisa ficar verificável.
      data-variant={isDark ? "dark" : "light"}
    >
      {imagem}
      {/* A luz. Vazio e `aria-hidden` implícito por não ter conteúdo: é pintura,
          não conteúdo, e o `pointer-events: none` do CSS o mantém fora do
          caminho do clique. */}
      <span className="acertei-wordmark__shine" />
    </Link>
  );
}

function cx(...v: Array<string | false | null | undefined>): string {
  return v.filter(Boolean).join(" ");
}
